import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, BookOpen, Award, Globe } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const EventsPage = () => {
  const eventSeries = [
    {
      title: "Ursula Graham Bower Lectures",
      icon: Award,
      image: "https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/nrrl7lfn_Ursula%20Graham%20Bower.jpg",
      description: "The Highland Institute's most prestigious annual academic event, honouring a pioneering anthropologist",
      details: "Held annually in December in Kohima, featuring distinguished scholars in anthropology, indigenous studies, and environmental humanities",
      link: "/bower-lectures"
    },
    {
      title: "British Academy Workshops",
      icon: BookOpen,
      image: "https://customer-assets.emergentagent.com/job_vegr-highland/artifacts/348rwxzu_British%20Academy%20Workshops.jpeg",
      description: "Comprehensive academic writing training programme for early-career scholars",
      details: "2020-2022 series focusing on research methods, peer review, publishing, and academic networking",
      link: "/events"
    },
    {
      title: "Ekologos Conference Series",
      icon: Globe,
      image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/kp2xwc0c_SAS%20Environmental%20Studies.jpeg",
      description: "Global Environmental Humanities conferences bringing together interdisciplinary researchers",
      details: "Annual conferences on climate change, indigenous knowledge, and environmental resilience",
      link: "/events"
    },
    {
      title: "Colloquia & Symposia",
      icon: Users,
      image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/ww4wtg1g_SAS%20Philosophy.JPG",
      description: "Regular academic gatherings fostering interdisciplinary dialogue",
      details: "Intimate research presentations to multi-day symposia, open to the public",
      link: "/events"
    }
  ];

  const upcomingEvents = [
    {
      title: "Ursula Graham Bower Lectures 2025",
      date: "December 2025",
      location: "Kohima, Nagaland",
      category: "Annual Lecture",
      description: "The 13th Annual Bower Lectures featuring a distinguished scholar and the Winter Research Colloquium"
    },
    {
      title: "Summer School 2025",
      date: "June 2025",
      location: "Kohima, Nagaland",
      category: "Training Programme",
      description: "Intensive residential training module for postgraduate researchers"
    },
    {
      title: "Ekologos Conference 2025",
      date: "To be announced",
      location: "Various Locations",
      category: "Conference",
      description: "Global Environmental Humanities gathering across partner institutions"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Events & Lecture Series
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Fostering dialogue, celebrating scholarship, and engaging communities through academic events
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Upcoming Events</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {upcomingEvents.map((event, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                      {event.category}
                    </span>
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <CardTitle className="text-xl">{event.title}</CardTitle>
                  <CardDescription>
                    <div className="text-sm text-gray-600 mt-2">
                      <p className="font-medium">{event.date}</p>
                      <p>{event.location}</p>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 text-sm">{event.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/events/calendar">
              <Button className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg">
                View Full Calendar
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Major Event Series */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Major Event Series</h2>
            <div className="h-1 w-24 bg-green-600"></div>
            <p className="text-gray-600 mt-4 max-w-3xl">
              The Highland Institute hosts a range of prestigious annual events, workshops, and conferences that bring 
              together leading scholars, researchers, and community partners from around the world.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {eventSeries.map((series, index) => {
              const IconComponent = series.icon;
              return (
                <Card key={index} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={series.image}
                      alt={series.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center mb-2">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <IconComponent className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-white">{series.title}</h3>
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardDescription className="text-gray-700 text-base">
                      {series.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm mb-6">{series.details}</p>
                    <Link to={series.link}>
                      <Button className="w-full bg-green-600 hover:bg-green-700 group-hover:scale-105 transition-transform">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Types of Events</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Annual Lectures</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Prestigious keynote lectures by distinguished scholars addressing critical issues in highland studies
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Workshops & Training</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Intensive skill-building sessions on research methods, writing, and professional development
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Conferences & Symposia</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Multi-day academic gatherings bringing together researchers from around the world
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Past Events Highlights */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Past Events Highlights</h2>
            <div className="space-y-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center">
                    <Calendar className="w-6 h-6 text-green-600 mr-3" />
                    Bower Lectures 2024
                  </CardTitle>
                  <CardDescription>Dr Bérénice Guyot-Réchard</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-2">
                    <strong>"Tangled Lands: Burma and India's Unfinished Separation"</strong>
                  </p>
                  <p className="text-gray-600 text-sm">
                    Reader in International and South Asian History at King's College London delivered the 12th Annual 
                    Ursula Graham Bower Lectures, exploring the historical complexities of the India-Myanmar border.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center">
                    <Calendar className="w-6 h-6 text-green-600 mr-3" />
                    British Academy Workshops 2022
                  </CardTitle>
                  <CardDescription>December 12-14, 2022</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-2">
                    <strong>Academic Writing & Research Capacity Building</strong>
                  </p>
                  <p className="text-gray-600 text-sm">
                    Final in-person workshop brought together 27 participants for paper presentations, one-to-one 
                    mentorship, and training sessions led by scholars from Edinburgh, Manchester, and IIT Guwahati.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center">
                    <Calendar className="w-6 h-6 text-green-600 mr-3" />
                    Ekologos Conference 2023
                  </CardTitle>
                  <CardDescription>Global Environmental Humanities</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-2">
                    <strong>Climate Change & Indigenous Knowledge Systems</strong>
                  </p>
                  <p className="text-gray-600 text-sm">
                    Interdisciplinary gathering convening researchers from Norway, Brazil, India and beyond to address 
                    critical climate challenges in polar, tropical, and highland regions.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Stay Connected</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Get updates on upcoming events, lectures, and conferences at the Highland Institute
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="mailto:info@highlandinstitute.org">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Subscribe to Updates
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Events Team
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
